import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Download, AlertCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const Admin = () => {
  const { user } = useAuth();
  const [settings, setSettings] = useState({ cycle_name: '', cycle_year: '' });
  const [users, setUsers] = useState([]);
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cleaningData, setCleaningData] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [archiveYear, setArchiveYear] = useState('');
  const [availableYears, setAvailableYears] = useState([]);
  
  // User form state
  const [userForm, setUserForm] = useState({
    id: '',
    name: '',
    email: '',
    password: '',
    role: 'EMPLOYEE',
    department: '',
    manager_id: ''
  });
  const [editingUserId, setEditingUserId] = useState(null);

  useEffect(() => {
    if (user?.role !== 'HR_ADMIN') {
      toast.error('Access denied. HR Admin only.');
      return;
    }
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [settingsRes, usersRes, managersRes, appraisalsRes] = await Promise.all([
        axios.get(`${API_URL}/settings`),
        axios.get(`${API_URL}/users`),
        axios.get(`${API_URL}/users/managers`),
        axios.get(`${API_URL}/appraisals`)
      ]);
      
      setSettings(settingsRes.data);
      setUsers(usersRes.data);
      setManagers(managersRes.data);
      
      // Get unique years from active appraisals for archive dropdown
      const years = [...new Set(appraisalsRes.data.map(a => a.cycle_year))].sort().reverse();
      setAvailableYears(years);
      if (years.length > 0) {
        setArchiveYear(years[0]);
      }
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      await axios.put(`${API_URL}/settings`, settings);
      toast.success('Settings saved successfully');
    } catch (error) {
      toast.error('Failed to save settings');
    }
  };

  const handleClearUserForm = () => {
    setUserForm({
      id: '',
      name: '',
      email: '',
      password: '',
      role: 'EMPLOYEE',
      department: '',
      manager_id: ''
    });
    setEditingUserId(null);
  };

  const handleEditUser = (userData) => {
    setUserForm({
      id: userData.id,
      name: userData.name,
      email: userData.email,
      password: '',
      role: userData.role,
      department: userData.department || '',
      manager_id: userData.manager_id || ''
    });
    setEditingUserId(userData.id);
  };

  const handleSaveUser = async () => {
    try {
      if (!userForm.name || !userForm.email) {
        toast.error('Name and email are required');
        return;
      }

      if (!editingUserId && !userForm.password) {
        toast.error('Password is required for new users');
        return;
      }

      if (editingUserId) {
        // Update existing user
        const updateData = {
          name: userForm.name,
          email: userForm.email,
          role: userForm.role,
          department: userForm.department || null,
          manager_id: userForm.manager_id || null
        };
        await axios.put(`${API_URL}/users/${editingUserId}`, updateData);
        toast.success('User updated successfully');
      } else {
        // Create new user
        await axios.post(`${API_URL}/users`, userForm);
        toast.success('User created successfully');
      }

      handleClearUserForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save user');
    }
  };

  const handleDeleteUser = async () => {
    if (!editingUserId) {
      toast.error('Please select a user to delete');
      return;
    }

    if (!window.confirm(`Are you sure you want to delete ${userForm.name}?`)) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/users/${editingUserId}`);
      toast.success('User deleted successfully');
      handleClearUserForm();
      fetchData();
    } catch (error) {
      toast.error('Failed to delete user');
    }
  };

  const handleExportData = async () => {
    try {
      const response = await axios.get(`${API_URL}/export/json`);
      const dataStr = JSON.stringify(response.data, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `mestastop-backup-${new Date().toISOString()}.json`;
      link.click();
      toast.success('Data exported successfully');
    } catch (error) {
      toast.error('Failed to export data');
    }
  };

  const handleCleanupTestData = async () => {
    if (!window.confirm(
      'This will remove all test users (David, Emily, Jessica, Michael, old admin) and all appraisals.\n\n' +
      'Only Arnab and Debabani will remain.\n\n' +
      'This action cannot be undone. Continue?'
    )) {
      return;
    }

    setCleaningData(true);
    try {
      const response = await axios.post(`${API_URL}/admin/cleanup-test-users`);
      toast.success(`Cleanup complete! Removed ${response.data.users_deleted} test users and all appraisals`);
      fetchData();
    } catch (error) {
      toast.error('Failed to cleanup test data');
    } finally {
      setCleaningData(false);
    }
  };

  const handleArchiveAppraisals = async () => {
    if (!archiveYear) {
      toast.error('Please select a year to archive');
      return;
    }

    if (!window.confirm(
      `This will archive all appraisals for cycle year ${archiveYear}.\n\n` +
      'Archived appraisals will be:\n' +
      '- Moved to "Archived Appraisals" page\n' +
      '- Read-only (no further edits)\n' +
      '- Available for PDF download\n\n' +
      'This action cannot be undone. Continue?'
    )) {
      return;
    }

    setArchiving(true);
    try {
      const response = await axios.post(`${API_URL}/appraisals/archive/${archiveYear}`);
      toast.success(response.data.message);
      fetchData(); // Refresh to update available years
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to archive appraisals');
    } finally {
      setArchiving(false);
    }
  };

  const getRoleLabel = (role) => {
    const labels = {
      'EMPLOYEE': 'Employee',
      'MANAGER': 'Manager',
      'HR_ADMIN': 'HR Admin'
    };
    return labels[role] || role;
  };

  if (user?.role !== 'HR_ADMIN') {
    return (
      <div className="text-center p-8">
        <p className="text-slate-700">Access denied. This page is only accessible to HR Admins.</p>
      </div>
    );
  }

  if (loading) return <div className="text-sm text-slate-700">Loading...</div>;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl tracking-tight font-semibold text-slate-900" data-testid="admin-title">Admin Panel</h1>
        <p className="text-sm text-slate-700 mt-2">Configure cycle settings and manage users</p>
      </div>

      {/* Cycle Settings */}
      <Card className="border border-slate-200 bg-white rounded-sm p-6">
        <h2 className="text-2xl tracking-tight font-semibold text-slate-900 mb-4">Cycle Settings</h2>
        <p className="text-sm text-slate-700 mb-4">Configure the default cycle for new appraisals</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
          <div>
            <Label htmlFor="cycle-name" className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2 block">
              Current Cycle Name
            </Label>
            <Input
              id="cycle-name"
              value={settings.cycle_name}
              onChange={(e) => setSettings({ ...settings, cycle_name: e.target.value })}
              className="border-slate-200 rounded-sm"
              data-testid="cycle-name-input"
              placeholder="e.g., H1 2026"
            />
          </div>
          
          <div>
            <Label htmlFor="cycle-year" className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2 block">
              Cycle Year
            </Label>
            <Input
              id="cycle-year"
              value={settings.cycle_year}
              onChange={(e) => setSettings({ ...settings, cycle_year: e.target.value })}
              className="border-slate-200 rounded-sm"
              data-testid="cycle-year-input"
              placeholder="e.g., 2026"
            />
          </div>
        </div>
        
        <Button
          onClick={handleSaveSettings}
          className="bg-[#1E40AF] hover:bg-[#1E3A8A] text-white rounded-sm"
          data-testid="save-settings-button"
        >
          Save Settings
        </Button>
      </Card>

      {/* User Management */}
      <Card className="border border-slate-200 bg-white rounded-sm p-6 space-y-6">
        <div>
          <h2 className="text-2xl tracking-tight font-semibold text-slate-900">User Management</h2>
          <p className="text-sm text-slate-700 mt-1">Add people, assign roles, and link employees to managers</p>
        </div>

        {/* User Form */}
        <div className="border border-slate-200 bg-slate-50 rounded-sm p-4 space-y-4">
          <h3 className="text-sm font-semibold text-slate-900">
            {editingUserId ? 'Edit User' : 'Add New User'}
          </h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="user-name" className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2 block">
                Name
              </Label>
              <Input
                id="user-name"
                value={userForm.name}
                onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                className="border-slate-200 rounded-sm"
                data-testid="user-name-input"
                placeholder="John Doe"
              />
            </div>
            
            <div>
              <Label htmlFor="user-email" className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2 block">
                Email
              </Label>
              <Input
                id="user-email"
                type="email"
                value={userForm.email}
                onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                className="border-slate-200 rounded-sm"
                data-testid="user-email-input"
                placeholder="john@company.com"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="user-password" className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2 block">
                Password {editingUserId && '(leave blank to keep unchanged)'}
              </Label>
              <Input
                id="user-password"
                type="password"
                value={userForm.password}
                onChange={(e) => setUserForm({ ...userForm, password: e.target.value })}
                className="border-slate-200 rounded-sm"
                data-testid="user-password-input"
                placeholder={editingUserId ? "Leave blank to keep current" : "••••••••"}
              />
            </div>
            
            <div>
              <Label htmlFor="user-role" className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2 block">
                Role
              </Label>
              <Select
                value={userForm.role}
                onValueChange={(value) => setUserForm({ ...userForm, role: value })}
              >
                <SelectTrigger className="border-slate-200 rounded-sm" data-testid="user-role-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="EMPLOYEE">Employee</SelectItem>
                  <SelectItem value="MANAGER">Manager</SelectItem>
                  <SelectItem value="HR_ADMIN">HR Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="user-department" className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2 block">
                Department
              </Label>
              <Input
                id="user-department"
                value={userForm.department}
                onChange={(e) => setUserForm({ ...userForm, department: e.target.value })}
                className="border-slate-200 rounded-sm"
                data-testid="user-department-input"
                placeholder="e.g., Engineering, Sales"
              />
            </div>
            
            <div>
              <Label htmlFor="user-manager" className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2 block">
                Manager
              </Label>
              <Select
                value={userForm.manager_id || "none"}
                onValueChange={(value) => setUserForm({ ...userForm, manager_id: value === "none" ? "" : value })}
              >
                <SelectTrigger className="border-slate-200 rounded-sm" data-testid="user-manager-select">
                  <SelectValue placeholder="-- none / not applicable --" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">-- none / not applicable --</SelectItem>
                  {managers.map((manager) => (
                    <SelectItem key={manager.id} value={manager.id}>
                      {manager.name} ({getRoleLabel(manager.role)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex gap-4">
            <Button
              onClick={handleSaveUser}
              className="bg-[#1E40AF] hover:bg-[#1E3A8A] text-white rounded-sm"
              data-testid="save-user-button"
            >
              {editingUserId ? 'Update User' : 'Create User'}
            </Button>
            <Button
              onClick={handleClearUserForm}
              variant="outline"
              className="border-slate-200 rounded-sm"
              data-testid="clear-user-form-button"
            >
              Clear
            </Button>
            {editingUserId && (
              <Button
                onClick={handleDeleteUser}
                variant="destructive"
                className="rounded-sm"
                data-testid="delete-user-button"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete User
              </Button>
            )}
          </div>
        </div>

        {/* Users Table */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="users-table">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Name</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Email</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Role</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Department</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Manager</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Action</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center p-8 text-slate-500">No users found</td>
                </tr>
              ) : (
                users.map((userData) => (
                  <tr key={userData.id} className="border-b border-slate-200 hover:bg-slate-50 transition-colors duration-200" data-testid={`user-row-${userData.id}`}>
                    <td className="p-4 text-slate-700">{userData.name}</td>
                    <td className="p-4 text-slate-700">{userData.email}</td>
                    <td className="p-4 text-slate-700">{getRoleLabel(userData.role)}</td>
                    <td className="p-4 text-slate-700">{userData.department || '-'}</td>
                    <td className="p-4 text-slate-700">{userData.manager_name || '-'}</td>
                    <td className="p-4">
                      <Button
                        onClick={() => handleEditUser(userData)}
                        variant="outline"
                        size="sm"
                        className="border-slate-200 rounded-sm"
                        data-testid={`edit-user-${userData.id}`}
                      >
                        <Edit2 className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Export / Backup */}
      <Card className="border border-slate-200 bg-white rounded-sm p-6">
        <h2 className="text-2xl tracking-tight font-semibold text-slate-900 mb-4">Data Management</h2>
        
        <div className="space-y-6">
          {/* Archive Section */}
          <div>
            <div className="flex items-start gap-3 mb-4">
              <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5" />
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Archive Appraisals</h3>
                <p className="text-sm text-slate-700 mb-2">
                  Archive all appraisals for a completed cycle year. Archived appraisals will:
                </p>
                <ul className="text-sm text-slate-700 space-y-1 ml-5">
                  <li>• Become read-only (no further edits allowed)</li>
                  <li>• Move to "Archived Appraisals" page organized by year</li>
                  <li>• Remain available for PDF download</li>
                  <li>• Help keep active appraisals organized</li>
                </ul>
              </div>
            </div>
            <div className="flex gap-3 items-end">
              <div className="flex-1">
                <Label className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500 mb-2 block">
                  Select Year to Archive
                </Label>
                <Select value={archiveYear} onValueChange={setArchiveYear}>
                  <SelectTrigger className="border-slate-200 rounded-sm">
                    <SelectValue placeholder="Select year" />
                  </SelectTrigger>
                  <SelectContent>
                    {availableYears.length === 0 ? (
                      <SelectItem value="none" disabled>No years available</SelectItem>
                    ) : (
                      availableYears.map(year => (
                        <SelectItem key={year} value={year}>{year}</SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
              <Button
                onClick={handleArchiveAppraisals}
                disabled={archiving || availableYears.length === 0}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-sm"
                data-testid="archive-button"
              >
                {archiving ? 'Archiving...' : `Archive ${archiveYear}`}
              </Button>
            </div>
          </div>

          {/* Export Section */}
          <div className="pt-6 border-t border-slate-200">
            <p className="text-sm text-slate-700 mb-4">Export all data (settings, users, appraisals) as JSON for backup or migration</p>
            <Button
              onClick={handleExportData}
              variant="outline"
              className="border-slate-200 rounded-sm"
              data-testid="export-button"
            >
              <Download className="mr-2 h-4 w-4" />
              Download JSON Backup
            </Button>
          </div>

          {/* Cleanup Section */}
          <div className="pt-6 border-t border-slate-200">
            <div className="flex items-start gap-3 mb-4">
              <AlertCircle className="h-5 w-5 text-orange-600 mt-0.5" />
              <div>
                <h3 className="text-lg font-semibold text-slate-900 mb-2">Clean Test Data</h3>
                <p className="text-sm text-slate-700 mb-2">
                  Remove test users and appraisals from the database. This will:
                </p>
                <ul className="text-sm text-slate-700 space-y-1 ml-5">
                  <li>• Delete test users: David, Emily, Jessica, Michael, old admin</li>
                  <li>• Keep production users: Arnab and Debabani</li>
                  <li>• Remove all test appraisals</li>
                  <li>• Give you a clean database for real employees</li>
                </ul>
              </div>
            </div>
            <Button
              onClick={handleCleanupTestData}
              disabled={cleaningData}
              variant="destructive"
              className="rounded-sm"
              data-testid="cleanup-button"
            >
              <Trash2 className="mr-2 h-4 w-4" />
              {cleaningData ? 'Cleaning...' : 'Clean Test Data'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default Admin;
